import { addDaysUtc, parseTimeToMinutes } from './functions.js';
import { isoDateFromTimestamp } from './protocol-cycles.js';
const START_TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const MINUTES_PER_DAY = 1440;
// Prescription statuses after which nothing more happens to a treatment; a protocol whose treatments all reached one is completed.
const FINAL_TREATMENT_STATUSES = new Set(['administered', 'discarded']);
/**
 * The ISO dates (sorted, unique) on which a protocol about to be assigned plans a treatment.
 * Mirrors the date logic of buildCycleTherapySequences (assign-patient-protocol.ts): evenly spaced cycles, template day offset and the start-time shift rolling past midnight.
 */
export function getPlannedTreatmentDates(input) {
    const { startDate, cycles, daysRepetition, sequences, removedSequenceIds = [], startTime, dayZeroProtocol } = input;
    if (!Number.isFinite(startDate) || !Number.isInteger(cycles) || cycles < 1)
        return [];
    const kept = sequences.filter(seq => !removedSequenceIds.includes(seq.id));
    if (!kept.length)
        return [];
    // Notes take part in finding the earliest template time, exactly as in assignPatientProtocol.
    const earliestTemplateMinutes = Math.min(...kept.map(seq => parseTimeToMinutes(seq.time)));
    const startTimeDeltaMinutes = startTime && START_TIME_PATTERN.test(startTime) ? parseTimeToMinutes(startTime) - earliestTemplateMinutes : 0;
    const dates = new Set();
    for (const seq of kept) {
        if (seq.isNote)
            continue;
        const timeDayOffset = Math.floor((parseTimeToMinutes(seq.time) + startTimeDeltaMinutes) / MINUTES_PER_DAY);
        const dayOffset = (dayZeroProtocol ? seq.day : seq.day - 1) + timeDayOffset;
        for (let cycleIndex = 0; cycleIndex < cycles; cycleIndex++) {
            const cycleStart = addDaysUtc(startDate, cycleIndex * daysRepetition);
            dates.add(isoDateFromTimestamp(addDaysUtc(cycleStart, dayOffset)));
        }
    }
    return [...dates].toSorted();
}
function treatmentRows(patientProtocol) {
    return patientProtocol.therapySequence.filter(seq => seq.deletedAt == null && !seq.isNote && seq.status !== 'cancelled');
}
// Active means not removed (deletedAt), not stopped (endDate) and not completed (every treatment final).
export function isPatientProtocolActive(patientProtocol) {
    if (patientProtocol.deletedAt != null || patientProtocol.endDate != null)
        return false;
    const rows = treatmentRows(patientProtocol);
    if (!rows.length)
        return false;
    return rows.some(seq => !FINAL_TREATMENT_STATUSES.has(seq.status ?? ''));
}
// First and last planned treatment from the stored prescriptions, so moved cycles and removed rows are respected.
export function getPlannedTreatmentSpan(patientProtocol) {
    const dates = treatmentRows(patientProtocol)
        .map(seq => isoDateFromTimestamp(seq.therapyDate))
        .toSorted();
    if (!dates.length)
        return null;
    return { first: dates[0], last: dates[dates.length - 1] };
}
/**
 * Active protocols of the patient with a planned treatment between their first and last planned treatment (both inclusive) that coincides with one of the new protocol's planned dates.
 * Ordered by start date. Empty when the Active protocol warning setting is off; a missing setting counts as on (the column defaults to true).
 */
export function findOverlappingActiveProtocols(input) {
    const { enabled, newPlannedDates, patientProtocols } = input;
    if (enabled === false || !newPlannedDates.length)
        return [];
    const overlaps = [];
    for (const patientProtocol of patientProtocols) {
        if (!isPatientProtocolActive(patientProtocol))
            continue;
        const span = getPlannedTreatmentSpan(patientProtocol);
        if (!span)
            continue;
        // ISO dates compare correctly as strings.
        if (!newPlannedDates.some(date => date >= span.first && date <= span.last))
            continue;
        overlaps.push({
            patientProtocolId: patientProtocol.id,
            protocolName: patientProtocol.protocol?.name ?? '',
            startDate: patientProtocol.startDate,
            firstPlannedDate: span.first,
            lastPlannedDate: span.last,
        });
    }
    return overlaps.toSorted((a, b) => a.startDate - b.startDate ||
        a.firstPlannedDate.localeCompare(b.firstPlannedDate) ||
        a.patientProtocolId.localeCompare(b.patientProtocolId));
}
//# sourceMappingURL=protocol-overlap.js.map